// Chemin: C:\gestion-copro\src\pages\apporteur\ApporteurPipelinePage.tsx
// Session 188 : Pipeline de prospection — vue apporteur
// Session 211 : Fix chargement prospects (chargerProspectsParResponsable) + onglet Guide
// Session 223 : Harmonisation sur CrmPipelinePage — ajout onglets Actions du jour, Messagerie, Prospects
//               Navigation vers fiche prospect via /apporteur/crm/prospect/:id
//               Supprimé : onglet Tableau de bord, filtre responsable, Purge, Seed, CreerApporteurModal
//               Filtré : uniquement les prospects de l'apporteur connecté

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  User,
  Phone,
  Mail,
  Globe,
  MapPin,
  ChevronDown,
  ChevronUp,
  GripVertical,
  MessageSquarePlus,
  Trash2,
  Edit3,
  X,
  Check,
  Users,
  Building2,
  Megaphone,
  ExternalLink,
  StickyNote,
  RefreshCw,
  ArrowRightLeft,
  Target,
  AlertTriangle,
  CalendarClock,
  Sparkles,
  Send,
  Copy,
  RotateCcw,
  Inbox,
  List,
  Kanban,
  Clock,
  CheckCircle2,
  Eye,
  ChevronRight,
  BookOpen,
  AlertCircle,
  Zap,
  FileText,
} from 'lucide-react';
import {
  Prospect,
  StatutProspect,
  TypeProspect,
  TypeCibleProspect,
  ResponsableProspect,
  SegmentProspect,
  NoteSuivi,
  HistoriqueMailProspect,
  InfoResponsable,
  LABELS_STATUT_PROSPECT,
  COULEURS_STATUT_PROSPECT,
  COLONNES_KANBAN,
  LABELS_TYPE_CIBLE,
  LABELS_TYPE_PROSPECT,
  LABELS_TYPE_TEMPLATE,
  LABELS_SEGMENT_PROSPECT,
  COULEURS_SEGMENT_PROSPECT,
  DESCRIPTIONS_SEGMENT_PROSPECT,
  TypeTemplateMail,
} from '../../types/crm.types';
import {
  chargerProspectsParResponsable,
  getLabelResponsable,
  getCouleurResponsable,
  creerProspect,
  changerStatutProspect,
  mettreAJourProspect,
  ajouterNote,
  rapprocherProspectUser,
  ajouterHistoriqueMail,
  marquerMailEnvoye,
  supprimerMailHistorique,
} from '../../services/crm.service';
import { useAuth } from '../../context/AuthContext';
import { Timestamp } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import {
  genererMailProspection,
  ResultatMailProspection,
} from '../../services/prospection.service';

// ============================================
// TYPES ONGLETS
// ============================================

type OngletApporteur = 'pipeline' | 'actions-du-jour' | 'messagerie' | 'prospects' | 'guide';

// ============================================
// ICÔNES PAR TYPE DE PROSPECT
// ============================================

const ICONES_TYPE_PROSPECT: Record<TypeProspect, React.ReactNode> = {
  association: <Users size={14} className="text-purple-500" />,
  copropriete: <Building2 size={14} className="text-blue-500" />,
  individu: <User size={14} className="text-green-500" />,
  prescripteur: <Megaphone size={14} className="text-amber-500" />,
};

const SUGGESTIONS_PROBLEMATIQUES = [
  'Syndic opaque / manque de transparence',
  'Charges excessives ou injustifiées',
  'Travaux mal gérés ou non votés',
  'Absence de communication du syndic',
  'AG mal organisées ou non conformes',
  'Impayés importants dans la copropriété',
  'Dégradation des parties communes',
  'Conflit avec le promoteur / constructeur',
  'Problèmes de garantie décennale / GPA',
  'Syndic bénévole débordé',
];

// ============================================
// UTILITAIRES
// ============================================

const getDerniereActivite = (prospect: Prospect): Date => {
  const mails = prospect.historiqueMails || [];
  if (mails.length > 0) {
    const dates = mails
      .map((m) => m.date?.toDate?.() || new Date(m.date as unknown as string))
      .filter((d) => !isNaN(d.getTime()));
    if (dates.length > 0) return new Date(Math.max(...dates.map((d) => d.getTime())));
  }
  return prospect.createdAt?.toDate?.() || new Date(prospect.createdAt as unknown as string) || new Date();
};

const hasReponseEnAttente = (prospect: Prospect): boolean => {
  const mails = prospect.historiqueMails || [];
  const reponses = mails.filter((m) => (m as any).type === 'recu');
  if (reponses.length === 0) return false;
  const derniereReponse = reponses[reponses.length - 1];
  const dateReponse = derniereReponse.date?.toDate?.() || new Date(derniereReponse.date as unknown as string);
  const relanceApres = mails.some((m) => {
    if ((m as any).type === 'recu') return false;
    const d = m.date?.toDate?.() || new Date(m.date as unknown as string);
    return d > dateReponse;
  });
  return !relanceApres;
};

// ============================================
// CARTE PROSPECT (Kanban)
// ============================================

interface CarteProspectProps {
  prospect: Prospect;
  responsables: InfoResponsable[];
  onDragStart: (e: React.DragEvent, prospect: Prospect) => void;
  onOuvrirDetail: (prospect: Prospect) => void;
}

const CarteProspect: React.FC<CarteProspectProps> = ({
  prospect,
  responsables,
  onDragStart,
  onOuvrirDetail,
}) => {
  const nbNotes = prospect.notes?.length || 0;
  const nbMails = prospect.historiqueMails?.length || 0;

  const derniereActivite = getDerniereActivite(prospect);
  const joursInactivite = Math.floor((Date.now() - derniereActivite.getTime()) / (1000 * 60 * 60 * 24));
  const alerteInactivite = joursInactivite >= 14;
  const alerteReponse = hasReponseEnAttente(prospect);

  const tierBordure: Record<string, string> = {
    ambassadeur: 'border-l-4 border-l-emerald-500',
    client_direct: 'border-l-4 border-l-purple-400',
    recommandeur: 'border-l-4 border-l-blue-400',
  };
  const tierBadge: Record<string, string> = {
    ambassadeur: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    client_direct: 'bg-purple-100 text-purple-700 border-purple-300',
    recommandeur: 'bg-blue-100 text-blue-700 border-blue-300',
  };
  const bordureClasse = prospect.segment ? tierBordure[prospect.segment] : 'border-l-4 border-l-gray-200';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, prospect)}
      onClick={() => onOuvrirDetail(prospect)}
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all group ${bordureClasse}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <GripVertical size={16} className="text-gray-300 mt-0.5 flex-shrink-0 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {ICONES_TYPE_PROSPECT[prospect.typeProspect]}
            <span className="text-xs text-gray-400 font-medium">{LABELS_TYPE_PROSPECT[prospect.typeProspect]}</span>
          </div>
          <p className="font-semibold text-sm text-gray-800 truncate">{prospect.nom}</p>
          {prospect.contactNom && <p className="text-xs text-gray-500 truncate">{prospect.contactNom}</p>}
        </div>
      </div>

      {prospect.segment && (
        <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded border font-bold mb-2 ${tierBadge[prospect.segment] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
          {LABELS_SEGMENT_PROSPECT[prospect.segment as SegmentProspect]}
        </span>
      )}

      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-2 text-[10px] text-gray-400">
          {nbNotes > 0 && <span className="flex items-center gap-0.5"><StickyNote size={10} />{nbNotes}</span>}
          {nbMails > 0 && <span className="flex items-center gap-0.5"><Mail size={10} />{nbMails}</span>}
        </div>
        <div className="flex items-center gap-1">
          {alerteInactivite && <span title={`${joursInactivite}j sans activité`} className="w-4 h-4 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-[9px] font-bold">!</span>}
          {alerteReponse && <span title="Réponse en attente de relance" className="w-4 h-4 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-[9px] font-bold">R</span>}
        </div>
      </div>
    </div>
  );
};

// ============================================
// COLONNE KANBAN
// ============================================

const LABELS_COLONNES: Record<StatutProspect, string> = {
  identifie: 'Identifié',
  contacte: 'Contacté',
  interesse: 'Intéressé',
  demo: 'Démo',
  converti: 'Converti',
  perdu: 'Perdu',
};

const COULEURS_COLONNES: Record<string, string> = {
  identifie: 'bg-gray-100 text-gray-600',
  contacte: 'bg-blue-100 text-blue-700',
  interesse: 'bg-amber-100 text-amber-700',
  demo: 'bg-purple-100 text-purple-700',
  converti: 'bg-emerald-100 text-emerald-700',
  perdu: 'bg-red-100 text-red-600',
};

interface ColonneKanbanProps {
  statut: StatutProspect;
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onDragStart: (e: React.DragEvent, prospect: Prospect) => void;
  onDrop: (statut: StatutProspect) => void;
  onOuvrirDetail: (prospect: Prospect) => void;
}

const ColonneKanban: React.FC<ColonneKanbanProps> = ({
  statut,
  prospects,
  responsables,
  onDragStart,
  onDrop,
  onOuvrirDetail,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      className={`flex-shrink-0 w-56 rounded-xl transition-colors ${isDragOver ? 'bg-emerald-50' : 'bg-gray-50'}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={() => { setIsDragOver(false); onDrop(statut); }}
    >
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${COULEURS_COLONNES[statut] || 'bg-gray-100 text-gray-600'}`}>
            {LABELS_COLONNES[statut] || statut}
          </span>
        </div>
        <span className="text-xs font-semibold text-gray-400">{prospects.length}</span>
      </div>
      <div className="px-2 pb-3 space-y-2 min-h-[120px]">
        {prospects.map((prospect) => (
          <CarteProspect
            key={prospect.id}
            prospect={prospect}
            responsables={responsables}
            onDragStart={onDragStart}
            onOuvrirDetail={onOuvrirDetail}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// MODAL PROSPECT (création/édition)
// Responsable verrouillé sur apporteurId
// Pas de bouton Supprimer
// ============================================

interface ModalProspectApporteurProps {
  prospect?: Prospect | null;
  responsables: InfoResponsable[];
  apporteurId: ResponsableProspect;
  onFermer: () => void;
  onSauvegarder: (data: Parameters<typeof creerProspect>[0]) => Promise<void>;
}

const ModalProspectApporteur: React.FC<ModalProspectApporteurProps> = ({
  prospect,
  responsables,
  apporteurId,
  onFermer,
  onSauvegarder,
}) => {
  const [nom, setNom] = useState(prospect?.nom || '');
  const [typeProspect, setTypeProspect] = useState<TypeProspect>(prospect?.typeProspect || 'association');
  const [typeCible, setTypeCible] = useState<TypeCibleProspect>(prospect?.typeCible || 'vigie');
  const [segment, setSegment] = useState<SegmentProspect>(prospect?.segment || 'ambassadeur');
  const [contactNom, setContactNom] = useState(prospect?.contactNom || '');
  const [contactEmail, setContactEmail] = useState(prospect?.contactEmail || '');
  const [contactTelephone, setContactTelephone] = useState(prospect?.contactTelephone || '');
  const [adresse, setAdresse] = useState(prospect?.adresse || '');
  const [siteWeb, setSiteWeb] = useState(prospect?.siteWeb || '');
  const [source, setSource] = useState(prospect?.source || '');
  const [noteInitiale, setNoteInitiale] = useState('');
  const [problematiques, setProblematiques] = useState<string[]>(prospect?.problematiques || []);
  const [nouvelleProblematique, setNouvelleProblematique] = useState('');
  const [objectifContact, setObjectifContact] = useState(prospect?.objectifContact || '');
  const [tailleStructure, setTailleStructure] = useState(prospect?.tailleStructure || '');
  const [prochaineAction, setProchaineAction] = useState(prospect?.prochaineAction || '');
  const [dateProchaineAction, setDateProchaineAction] = useState(
    prospect?.dateProchaineAction
      ? new Date(prospect.dateProchaineAction.toDate()).toISOString().split('T')[0]
      : ''
  );
  const [saving, setSaving] = useState(false);

  const ajouterProblematique = (texte: string) => {
    const trimmed = texte.trim();
    if (trimmed && !problematiques.includes(trimmed)) {
      setProblematiques([...problematiques, trimmed]);
    }
    setNouvelleProblematique('');
  };

  const handleSubmit = async () => {
    if (!nom.trim()) return;
    setSaving(true);
    try {
      await onSauvegarder({
        nom, typeProspect, typeCible, segment,
        responsable: apporteurId,
        contactNom: contactNom || undefined,
        contactEmail: contactEmail || undefined,
        contactTelephone: contactTelephone || undefined,
        adresse: adresse || undefined,
        siteWeb: siteWeb || undefined,
        source: source || undefined,
        noteInitiale: noteInitiale || undefined,
        problematiques,
        objectifContact: objectifContact || undefined,
        tailleStructure: tailleStructure || undefined,
        prochaineAction: prochaineAction || undefined,
        dateProchaineAction: dateProchaineAction ? Timestamp.fromDate(new Date(dateProchaineAction)) : undefined,
      });
      onFermer();
    } catch (err) {
      console.error('[CRM Apporteur] Erreur sauvegarde:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">
            {prospect ? 'Modifier le prospect' : 'Nouveau prospect'}
          </h2>
          <button onClick={onFermer} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nom / Raison sociale *</label>
            <input
              type="text" value={nom} onChange={(e) => setNom(e.target.value)} autoFocus
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: ARC Hauts-de-France"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Type</label>
              <select value={typeProspect} onChange={(e) => setTypeProspect(e.target.value as TypeProspect)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
                {Object.entries(LABELS_TYPE_PROSPECT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Produit visé</label>
              <select value={typeCible} onChange={(e) => setTypeCible(e.target.value as TypeCibleProspect)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
                {Object.entries(LABELS_TYPE_CIBLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-2">Segment stratégique</label>
            <div className="space-y-2">
              {(['ambassadeur', 'client_direct', 'recommandeur'] as SegmentProspect[]).map((t) => (
                <button key={t} type="button" onClick={() => setSegment(t)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${
                    segment === t
                      ? COULEURS_SEGMENT_PROSPECT[t] + ' border-current'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-gray-50'
                  }`}>
                  <p className="font-bold text-xs">{LABELS_SEGMENT_PROSPECT[t]}</p>
                  <p className="text-[10px] opacity-70 mt-0.5 leading-tight">{DESCRIPTIONS_SEGMENT_PROSPECT[t]}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contact</p>
            <div className="space-y-2">
              <input type="text" value={contactNom} onChange={(e) => setContactNom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Nom du contact (Président, Directeur...)" />
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Email" />
              <input type="tel" value={contactTelephone} onChange={(e) => setContactTelephone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Téléphone" />
              <input type="text" value={adresse} onChange={(e) => setAdresse(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Adresse / Ville" />
              <input type="url" value={siteWeb} onChange={(e) => setSiteWeb(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Site web (https://...)" />
            </div>
          </div>

          <div className="border-t pt-4 bg-amber-50/30 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-3">Données SENTINELLE</p>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Problématiques identifiées</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {problematiques.map((p, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded border border-red-200">
                    {p}
                    <button onClick={() => setProblematiques(problematiques.filter((_, j) => j !== i))} className="hover:text-red-900"><X size={10} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={nouvelleProblematique}
                  onChange={(e) => setNouvelleProblematique(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && ajouterProblematique(nouvelleProblematique)}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  placeholder="Saisir ou choisir ci-dessous..." />
                <button onClick={() => ajouterProblematique(nouvelleProblematique)}
                  disabled={!nouvelleProblematique.trim()}
                  className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-30 transition-colors">
                  <Plus size={16} />
                </button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {SUGGESTIONS_PROBLEMATIQUES.filter((s) => !problematiques.includes(s)).slice(0, 5).map((s) => (
                  <button key={s} onClick={() => ajouterProblematique(s)}
                    className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded hover:bg-amber-100 hover:text-amber-700 transition-colors">
                    + {s.length > 35 ? s.substring(0, 35) + '...' : s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Objectif de la prise de contact</label>
              <input type="text" value={objectifContact} onChange={(e) => setObjectifContact(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                placeholder="Ex: Partenariat, abonnement CS, essai VIGIE..." />
            </div>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Taille / Nombre d'adhérents ou de lots</label>
              <input type="text" value={tailleStructure} onChange={(e) => setTailleStructure(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                placeholder="Ex: 3 500 adhérents, 45 lots..." />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Prochaine action</label>
                <input type="text" value={prochaineAction} onChange={(e) => setProchaineAction(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  placeholder="Ex: Appeler, envoyer démo..." />
              </div>
              <div className="w-40">
                <label className="block text-xs text-gray-500 mb-1">Date prévue</label>
                <input type="date" value={dateProchaineAction} onChange={(e) => setDateProchaineAction(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500" />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Source (comment identifié ?)</label>
              <input type="text" value={source} onChange={(e) => setSource(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Ex: Recherche web, salon, réseau..." />
            </div>
            {!prospect && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Note initiale (optionnel)</label>
                <textarea value={noteInitiale} onChange={(e) => setNoteInitiale(e.target.value)} rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 resize-none"
                  placeholder="Contexte, infos utiles..." />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onFermer} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">Annuler</button>
          <button onClick={handleSubmit} disabled={!nom.trim() || saving}
            className="px-6 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
            {saving ? 'Enregistrement...' : prospect ? 'Modifier' : 'Créer le prospect'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MODAL MAIL SENTINELLE
// ============================================

interface ModalMailSentinelleProps {
  prospect: Prospect;
  onFermer: () => void;
  onMailGenere: (prospectId: string) => void;
  onGmailOuvert?: (prospect: Prospect) => void;
}

const ModalMailSentinelle: React.FC<ModalMailSentinelleProps> = ({ prospect, onFermer, onMailGenere, onGmailOuvert }) => {
  const [template, setTemplate] = useState<TypeTemplateMail>('premier_contact');
  const [instructionsLibres, setInstructionsLibres] = useState('');
  const [generation, setGeneration] = useState(false);
  const [resultat, setResultat] = useState<ResultatMailProspection | null>(null);
  const [erreur, setErreur] = useState('');
  const [copie, setCopie] = useState(false);
  const [envoiOk, setEnvoiOk] = useState(false);
  const [contenuEdite, setContenuEdite] = useState<string>('');

  useEffect(() => {
    if (prospect.historiqueMails?.length > 0) {
      setTemplate('relance_demo');
    } else if (prospect.segment === 'ambassadeur') {
      setTemplate('proposition_partenariat');
    } else {
      setTemplate('premier_contact');
    }
  }, [prospect]);

  const handleGenerer = async () => {
    setGeneration(true);
    setErreur('');
    setResultat(null);
    setEnvoiOk(false);
    try {
      const mail = await genererMailProspection(prospect.id, template, instructionsLibres || undefined);
      setResultat(mail);
      setContenuEdite(mail.contenuTexte || '');
    } catch (err: unknown) {
      setErreur(err instanceof Error ? err.message : 'Erreur lors de la generation.');
    } finally {
      setGeneration(false);
    }
  };

  const handleOuvrirGmail = async () => {
    if (!resultat || !prospect.contactEmail) return;
    const to = encodeURIComponent(prospect.contactEmail);
    const subject = encodeURIComponent(resultat.objet);
    const body = encodeURIComponent(contenuEdite || resultat.contenuTexte || '');
    window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
    try {
      await ajouterHistoriqueMail(prospect.id, {
        objet: resultat.objet,
        template,
        destinataire: prospect.contactEmail || '',
        auteur: prospect.responsable,
        envoye: true,
        apercu: contenuEdite || resultat.contenuTexte,
        contenuHtml: resultat.contenuHtml,
      });
      onMailGenere(prospect.id);
    } catch (err) {
      console.error('[CRM Apporteur] Erreur enregistrement mail envoye:', err);
    }
    setEnvoiOk(true);
  };

  const handleEnregistrer = async () => {
    if (!resultat) return;
    try {
      await ajouterHistoriqueMail(prospect.id, {
        objet: resultat.objet,
        template,
        destinataire: prospect.contactEmail || '',
        auteur: prospect.responsable,
        envoye: false,
        apercu: resultat.contenuTexte,
        contenuHtml: resultat.contenuHtml,
      });
      onMailGenere(prospect.id);
      onFermer();
    } catch (err) {
      console.error('[CRM Apporteur] Erreur enregistrement brouillon:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <div className="flex items-center gap-2 text-white">
            <Sparkles size={20} />
            <h2 className="font-bold">SENTINELLE — Mail pour {prospect.nom}</h2>
          </div>
          <button onClick={onFermer} className="p-1 text-white/80 hover:text-white rounded"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!resultat && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type de mail</label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: 'premier_contact' as TypeTemplateMail, label: 'Premier contact', desc: 'Premiere prise de contact' },
                    { value: 'relance_demo' as TypeTemplateMail, label: 'Relance / Demo', desc: 'Apres un echange' },
                    { value: 'proposition_partenariat' as TypeTemplateMail, label: 'Partenariat', desc: 'Pour associations' },
                    { value: 'libre' as TypeTemplateMail, label: 'Redaction libre', desc: 'Consignes personnalisees' },
                  ]).map((t) => (
                    <button key={t.value} onClick={() => setTemplate(t.value)}
                      className={`text-left p-3 rounded-lg border-2 transition-colors ${template === t.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <p className="text-sm font-semibold text-gray-800">{t.label}</p>
                      <p className="text-xs text-gray-500">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {template === 'libre' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Consignes pour SENTINELLE</label>
                  <textarea value={instructionsLibres} onChange={(e) => setInstructionsLibres(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Ex: Insister sur le fait qu'on peut se rencontrer au salon X..." />
                </div>
              )}

              <button onClick={handleGenerer} disabled={generation}
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {generation ? <><RefreshCw size={18} className="animate-spin" /> SENTINELLE redige...</> : <><Sparkles size={18} /> Generer le mail</>}
              </button>

              {erreur && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{erreur}</div>}
            </>
          )}

          {resultat && (
            <>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Objet</p>
                <p className="text-sm font-semibold text-gray-800 bg-gray-50 rounded-lg px-3 py-2">{resultat.objet}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Contenu — modifiable</p>
                <textarea
                  value={contenuEdite}
                  onChange={(e) => setContenuEdite(e.target.value)}
                  className="w-full h-64 px-3 py-2 border rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 resize-none leading-relaxed"
                />
              </div>

              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800 font-semibold">N'oubliez pas de joindre le PDF de presentation manuellement dans Gmail.</p>
              </div>

              {envoiOk ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <Check size={24} className="text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-700">Gmail ouvert — mail enregistre comme envoye.</p>
                  <button onClick={onFermer} className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors">Fermer</button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <button onClick={handleOuvrirGmail} disabled={!prospect.contactEmail}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm">
                      <ExternalLink size={16} /> Ouvrir dans Gmail
                    </button>
                    <button onClick={handleEnregistrer}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm">
                      <Check size={16} /> Brouillon
                    </button>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(`Objet : ${resultat.objet}\n\n${resultat.contenuTexte}`).then(() => { setCopie(true); setTimeout(() => setCopie(false), 2000); }); }}
                    className="w-full flex items-center justify-center gap-2 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors">
                    <Copy size={14} /> {copie ? 'Copie !' : 'Copier le texte'}
                  </button>
                  <button onClick={() => { setResultat(null); setErreur(''); }}
                    className="w-full flex items-center justify-center gap-2 py-2 text-gray-400 hover:text-gray-600 text-sm transition-colors">
                    <RotateCcw size={14} /> Regenerer
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// ONGLET MESSAGERIE
// ============================================

interface OngletMessagerieProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onOuvrirProspect: (prospect: Prospect) => void;
  onGenererMail: (prospect: Prospect) => void;
  onGmailOuvert?: (prospect: Prospect) => void;
  onSupprime?: () => void;
}

const OngletMessagerie: React.FC<OngletMessagerieProps> = ({
  prospects,
  responsables,
  onOuvrirProspect,
  onGenererMail,
  onGmailOuvert,
  onSupprime,
}) => {
  const [filtreMail, setFiltreMail] = useState<'tous' | 'brouillons' | 'envoyes'>('tous');
  const [filtreProspect, setFiltreProspect] = useState<string>('tous');
  const [mailOuvert, setMailOuvert] = useState<{ mail: HistoriqueMailProspect; prospect: Prospect } | null>(null);
  const [envoiOk, setEnvoiOk] = useState(false);
  const [marquerEnCours, setMarquerEnCours] = useState(false);
  const [confirmSupp, setConfirmSupp] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [texteEdite, setTexteEdite] = useState<string>('');
  const [modeEdition, setModeEdition] = useState(false);

  const tousLesMails: Array<{ mail: HistoriqueMailProspect; prospect: Prospect }> = [];
  prospects.forEach((prospect) => {
    (prospect.historiqueMails || []).forEach((mail) => {
      tousLesMails.push({ mail, prospect });
    });
  });
  tousLesMails.sort((a, b) => {
    try {
      return (b.mail.date?.toDate?.()?.getTime() || 0) - (a.mail.date?.toDate?.()?.getTime() || 0);
    } catch { return 0; }
  });

  const mailsFiltres = tousLesMails.filter(({ mail, prospect }) => {
    if (filtreMail === 'brouillons' && mail.envoye) return false;
    if (filtreMail === 'envoyes' && !mail.envoye) return false;
    if (filtreProspect !== 'tous' && prospect.id !== filtreProspect) return false;
    return true;
  });

  const nbBrouillons = tousLesMails.filter((m) => !m.mail.envoye).length;
  const nbEnvoyes = tousLesMails.filter((m) => m.mail.envoye).length;

  const formatDate = (ts?: Timestamp | null): string => {
    if (!ts) return '—';
    try {
      return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  };

  const handleOuvrirMail = (mail: HistoriqueMailProspect, prospect: Prospect) => {
    setMailOuvert({ mail, prospect });
    setTexteEdite(mail.apercu || '');
    setModeEdition(false);
    setEnvoiOk(false);
    setConfirmSupp(false);
  };

  const handleFermerModal = () => {
    setMailOuvert(null);
    setEnvoiOk(false);
    setConfirmSupp(false);
    setModeEdition(false);
    setTexteEdite('');
  };

  const handleOuvrirGmail = async () => {
    if (!mailOuvert || !mailOuvert.prospect.contactEmail) return;
    const to = encodeURIComponent(mailOuvert.prospect.contactEmail);
    const subject = encodeURIComponent(mailOuvert.mail.objet);
    const body = encodeURIComponent(texteEdite || mailOuvert.mail.apercu || '');
    window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
    try {
      await marquerMailEnvoye(mailOuvert.prospect.id, mailOuvert.mail.id);
    } catch (err) {
      console.error('[CRM Apporteur] Erreur marquer envoye:', err);
    }
    setEnvoiOk(true);
  };

  const handleMarquerEnvoye = async () => {
    if (!mailOuvert) return;
    setMarquerEnCours(true);
    try {
      await marquerMailEnvoye(mailOuvert.prospect.id, mailOuvert.mail.id);
      setEnvoiOk(true);
    } catch (err) {
      console.error('[CRM Apporteur] Erreur marquer envoye:', err);
    } finally {
      setMarquerEnCours(false);
    }
  };

  const handleSupprimerMail = async () => {
    if (!mailOuvert) return;
    setSuppressionEnCours(true);
    try {
      await supprimerMailHistorique(mailOuvert.prospect.id, mailOuvert.mail.id);
      handleFermerModal();
      onSupprime?.();
    } catch (err) {
      console.error('[CRM Apporteur] Erreur suppression mail:', err);
      setConfirmSupp(false);
    } finally {
      setSuppressionEnCours(false);
    }
  };

  if (tousLesMails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm">
        <Inbox size={48} className="text-gray-300 mb-4" />
        <h3 className="text-lg font-bold text-gray-700 mb-2">Aucun mail pour l'instant</h3>
        <p className="text-sm text-gray-500 text-center max-w-sm">
          Ouvre un prospect depuis le Pipeline, puis utilise SENTINELLE pour generer un premier mail.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl shadow-sm">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {([
            { value: 'tous', label: `Tous (${tousLesMails.length})` },
            { value: 'brouillons', label: `Brouillons (${nbBrouillons})` },
            { value: 'envoyes', label: `Envoyes (${nbEnvoyes})` },
          ] as const).map((f) => (
            <button key={f.value} onClick={() => setFiltreMail(f.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${filtreMail === f.value ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={filtreProspect} onChange={(e) => setFiltreProspect(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 text-gray-700 bg-white">
          <option value="tous">Tous les prospects</option>
          {prospects.filter((p) => (p.historiqueMails?.length || 0) > 0).map((p) => (
            <option key={p.id} value={p.id}>{p.nom}</option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {mailsFiltres.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <p className="text-sm text-gray-400">Aucun mail correspondant aux filtres</p>
          </div>
        ) : (
          mailsFiltres.map(({ mail, prospect }, index) => (
            <div key={`${prospect.id}-${mail.id}-${index}`}
              onClick={() => handleOuvrirMail(mail, prospect)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${mail.envoye ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {mail.envoye ? 'Envoye' : 'Brouillon'}
                    </span>
                    <span className="text-xs text-blue-600 font-semibold">{LABELS_TYPE_TEMPLATE[mail.template] || mail.template}</span>
                  </div>
                  <p className="font-semibold text-gray-800 text-sm truncate">{mail.objet}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <button onClick={(e) => { e.stopPropagation(); onOuvrirProspect(prospect); }}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1">
                      {ICONES_TYPE_PROSPECT[prospect.typeProspect]}
                      {prospect.nom}
                    </button>
                    {mail.destinataire && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Mail size={10} /> {mail.destinataire}
                      </span>
                    )}
                  </div>
                  {mail.apercu && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{mail.apercu}</p>}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className="text-[10px] text-gray-400">{formatDate(mail.date)}</span>
                  {!mail.envoye && prospect.contactEmail ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const to = encodeURIComponent(prospect.contactEmail!);
                        const subject = encodeURIComponent(mail.objet);
                        const body = encodeURIComponent(mail.apercu || '');
                        window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
                        try { await marquerMailEnvoye(prospect.id, mail.id); } catch { /* non bloquant */ }
                      }}
                      className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-semibold transition-colors">
                      <ExternalLink size={10} /> Gmail
                    </button>
                  ) : (
                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                      <Eye size={10} /> Ouvrir
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {mailOuvert && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
              <div className="flex items-center gap-2 text-white">
                <Mail size={18} />
                <div>
                  <h2 className="font-bold text-sm leading-tight">{mailOuvert.mail.objet}</h2>
                  <p className="text-blue-200 text-xs mt-0.5">{mailOuvert.prospect.nom} — {mailOuvert.mail.destinataire}</p>
                </div>
              </div>
              <button onClick={handleFermerModal} className="p-1 text-white/80 hover:text-white rounded"><X size={20} /></button>
            </div>

            <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-gray-50">
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${mailOuvert.mail.envoye ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {mailOuvert.mail.envoye ? 'Envoye' : 'Brouillon'}
              </span>
              <span className="text-xs text-blue-600 font-semibold">{LABELS_TYPE_TEMPLATE[mailOuvert.mail.template]}</span>
              <span className="text-xs text-gray-400">{formatDate(mailOuvert.mail.date)}</span>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {!mailOuvert.mail.envoye && (
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400">Texte envoye dans Gmail</p>
                  <button onClick={() => setModeEdition((v) => !v)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold transition-colors">
                    <Edit3 size={12} /> {modeEdition ? 'Apercu' : 'Modifier le texte'}
                  </button>
                </div>
              )}
              {!mailOuvert.mail.envoye && modeEdition ? (
                <textarea
                  value={texteEdite}
                  onChange={(e) => setTexteEdite(e.target.value)}
                  className="w-full h-72 px-3 py-2 border rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-blue-500 resize-none font-mono leading-relaxed"
                  placeholder="Contenu du mail..."
                />
              ) : mailOuvert.mail.contenuHtml ? (
                <div className="border rounded-lg overflow-hidden bg-white" style={{ minHeight: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                  <iframe srcDoc={mailOuvert.mail.contenuHtml} title="Contenu du mail" className="w-full border-0" style={{ minHeight: '380px' }} />
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  {mailOuvert.mail.apercu ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{mailOuvert.mail.apercu}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic text-center py-6">
                      Contenu non disponible — ce brouillon a ete genere avant la mise a jour.<br />
                      Genere un nouveau mail depuis la fiche prospect.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              {!mailOuvert.mail.envoye && !envoiOk && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-2">
                  <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800 font-semibold">N'oubliez pas de joindre le PDF de presentation manuellement dans Gmail.</p>
                </div>
              )}

              {envoiOk ? (
                <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <Check size={18} className="text-emerald-600" />
                  <p className="text-sm font-semibold text-emerald-700">Gmail ouvert — mail marque comme envoye</p>
                </div>
              ) : mailOuvert.mail.envoye ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Ce mail a deja ete envoye.</p>
                  <button onClick={handleFermerModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-colors">
                    Fermer
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    {mailOuvert.prospect.contactEmail && (
                      <button onClick={handleOuvrirGmail}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm">
                        <ExternalLink size={16} /> Ouvrir dans Gmail
                      </button>
                    )}
                    <button onClick={handleMarquerEnvoye} disabled={marquerEnCours}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm">
                      {marquerEnCours ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                      Marquer envoye
                    </button>
                    <button onClick={() => { handleFermerModal(); onGenererMail(mailOuvert.prospect); }}
                      className="flex items-center justify-center gap-2 py-2.5 px-4 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg font-semibold hover:bg-amber-100 transition-colors text-sm">
                      <Sparkles size={16} /> Regenerer
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-end">
                {confirmSupp ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-semibold">Supprimer ce mail ?</span>
                    <button onClick={handleSupprimerMail} disabled={suppressionEnCours}
                      className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors">
                      {suppressionEnCours ? 'Suppression...' : 'Confirmer'}
                    </button>
                    <button onClick={() => setConfirmSupp(false)}
                      className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300 transition-colors">
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmSupp(true)}
                    className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors">
                    <Trash2 size={13} /> Supprimer ce mail
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ONGLET PROSPECTS (liste tabulaire)
// ============================================

const ORDRE_SEGMENT: SegmentProspect[] = ['ambassadeur', 'client_direct', 'recommandeur'];

interface OngletProspectsProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onOuvrirDetail: (prospect: Prospect) => void;
  onCreer: () => void;
}

const OngletProspects: React.FC<OngletProspectsProps> = ({
  prospects,
  responsables,
  onOuvrirDetail,
  onCreer,
}) => {
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState<StatutProspect | 'tous'>('tous');

  const prospectsFiltres = prospects.filter((p) => {
    if (filtreStatut !== 'tous' && p.statut !== filtreStatut) return false;
    if (recherche) {
      const s = recherche.toLowerCase();
      return p.nom.toLowerCase().includes(s) ||
        (p.contactNom || '').toLowerCase().includes(s) ||
        (p.contactEmail || '').toLowerCase().includes(s);
    }
    return true;
  });

  const groupesParSegment = ORDRE_SEGMENT.map((segment) => ({
    segment,
    prospects: prospectsFiltres.filter((p) => p.segment === segment),
  })).filter((g) => g.prospects.length > 0);

  const sansSeg = prospectsFiltres.filter((p) => !p.segment || !ORDRE_SEGMENT.includes(p.segment as SegmentProspect));

  const formatDate = (ts?: Timestamp | null): string => {
    if (!ts) return '—';
    try { return ts.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  const SEPARATEUR_COULEURS: Record<SegmentProspect, string> = {
    ambassadeur: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    client_direct: 'bg-purple-50 border-purple-200 text-purple-700',
    recommandeur: 'bg-blue-50 border-blue-200 text-blue-700',
  };

  const renderLignes = (liste: Prospect[]) =>
    liste.map((prospect) => (
      <tr key={prospect.id} onClick={() => onOuvrirDetail(prospect)}
        className="hover:bg-gray-50 cursor-pointer transition-colors group">
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {ICONES_TYPE_PROSPECT[prospect.typeProspect]}
            <span className="font-semibold text-sm text-gray-800">{prospect.nom}</span>
          </div>
          {prospect.tailleStructure && (
            <p className="text-xs text-gray-400 mt-0.5 ml-5">{prospect.tailleStructure}</p>
          )}
        </td>
        <td className="px-4 py-3">
          <p className="text-sm text-gray-700">{prospect.contactNom || '—'}</p>
          {prospect.contactEmail && <p className="text-xs text-gray-400">{prospect.contactEmail}</p>}
        </td>
        <td className="px-4 py-3">
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-600">
            {LABELS_STATUT_PROSPECT[prospect.statut]}
          </span>
        </td>
        <td className="px-4 py-3">
          {prospect.segment ? (
            <span className={`text-xs px-2 py-0.5 rounded font-bold border ${COULEURS_SEGMENT_PROSPECT[prospect.segment]}`}>
              {LABELS_SEGMENT_PROSPECT[prospect.segment as SegmentProspect]}
            </span>
          ) : '—'}
        </td>
        <td className="px-4 py-3">
          {(prospect.historiqueMails?.length || 0) > 0 ? (
            <span className="flex items-center gap-1 text-sm text-gray-600">
              <Mail size={12} className="text-blue-400" />
              {prospect.historiqueMails!.length}
            </span>
          ) : '—'}
        </td>
        <td className="px-4 py-3 text-xs text-gray-400">{formatDate(prospect.createdAt)}</td>
        <td className="px-4 py-3">
          <ChevronRight size={16} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
        </td>
      </tr>
    ));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            placeholder="Rechercher..." />
        </div>
        <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value as StatutProspect | 'tous')}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white text-gray-700">
          <option value="tous">Tous les statuts</option>
          {Object.entries(LABELS_STATUT_PROSPECT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={onCreer}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold text-sm transition-colors">
          <Plus size={16} /> Nouveau
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {prospectsFiltres.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-400">Aucun prospect correspondant</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Prospect</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Contact</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Statut</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Segment</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Mails</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Cree le</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groupesParSegment.map(({ segment, prospects: groupe }) => (
                <React.Fragment key={segment}>
                  <tr>
                    <td colSpan={7} className="px-4 py-2">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold w-fit ${SEPARATEUR_COULEURS[segment]}`}>
                        {LABELS_SEGMENT_PROSPECT[segment]}
                        <span className="font-normal opacity-70">— {groupe.length} prospect{groupe.length > 1 ? 's' : ''}</span>
                      </div>
                    </td>
                  </tr>
                  {renderLignes(groupe)}
                </React.Fragment>
              ))}
              {sansSeg.length > 0 && (
                <React.Fragment>
                  <tr>
                    <td colSpan={7} className="px-4 py-2">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold w-fit bg-gray-50 border-gray-200 text-gray-500">
                        Non classes
                        <span className="font-normal opacity-70">— {sansSeg.length} prospect{sansSeg.length > 1 ? 's' : ''}</span>
                      </div>
                    </td>
                  </tr>
                  {renderLignes(sansSeg)}
                </React.Fragment>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============================================
// ONGLET ACTIONS DU JOUR
// ============================================

interface OngletActionsDuJourProps {
  prospects: Prospect[];
  responsables: InfoResponsable[];
  onOuvrirProspect: (p: Prospect) => void;
}

const OngletActionsDuJour: React.FC<OngletActionsDuJourProps> = ({
  prospects,
  responsables,
  onOuvrirProspect,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const actifs = prospects.filter((p) => p.statut !== 'converti' && p.statut !== 'perdu');

  const actionsAFaire = actifs
    .filter((p) => {
      if (!p.dateProchaineAction) return false;
      const d = p.dateProchaineAction.toDate();
      d.setHours(0, 0, 0, 0);
      return d <= today;
    })
    .sort((a, b) => a.dateProchaineAction!.toDate().getTime() - b.dateProchaineAction!.toDate().getTime());

  const seuilBrouillon = new Date(today.getTime() - 48 * 60 * 60 * 1000);
  const brouillonsEnAttente: Array<{ prospect: Prospect; mail: HistoriqueMailProspect }> = [];
  actifs.forEach((p) => {
    (p.historiqueMails || [])
      .filter((m) => !m.envoye && (!m.type || m.type === 'brouillon'))
      .forEach((m) => {
        const d = m.date?.toDate?.() || new Date(m.date as unknown as string);
        if (d < seuilBrouillon) brouillonsEnAttente.push({ prospect: p, mail: m });
      });
  });
  brouillonsEnAttente.sort((a, b) => (a.mail.date?.toDate?.().getTime() || 0) - (b.mail.date?.toDate?.().getTime() || 0));

  const seuilInactivite = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
  const inactifs = actifs
    .filter((p) => getDerniereActivite(p) < seuilInactivite)
    .sort((a, b) => getDerniereActivite(a).getTime() - getDerniereActivite(b).getTime());

  const reponsesEnAttente = actifs.filter((p) => hasReponseEnAttente(p));

  const total = actionsAFaire.length + brouillonsEnAttente.length + inactifs.length + reponsesEnAttente.length;

  const formatDateRelative = (d: Date): string => {
    const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    if (diff === 1) return 'hier';
    return `il y a ${diff}j`;
  };

  const formatDateAction = (ts: Timestamp): string => {
    const d = ts.toDate();
    const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    if (diff < 0) return `dans ${Math.abs(diff)}j`;
    return `retard ${diff}j`;
  };

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
          <CheckCircle2 size={32} className="text-emerald-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-1">Rien en attente</h3>
        <p className="text-sm text-gray-500">Bonne journee — tout est a jour !</p>
      </div>
    );
  }

  const SectionHeader: React.FC<{ icon: React.ReactNode; titre: string; count: number; couleur: string; bgCouleur: string }> = ({ icon, titre, count, couleur, bgCouleur }) => (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${bgCouleur} mb-2`}>
      <div className={couleur}>{icon}</div>
      <span className={`font-bold text-sm ${couleur}`}>{titre}</span>
      <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${couleur} bg-white/70`}>{count}</span>
    </div>
  );

  const LigneProspect: React.FC<{ prospect: Prospect; meta: string; metaCouleur: string; sousTitre?: string }> = ({ prospect, meta, metaCouleur, sousTitre }) => (
    <button
      onClick={() => onOuvrirProspect(prospect)}
      className="w-full flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all text-left group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-gray-800 truncate group-hover:text-emerald-700 transition-colors">{prospect.nom}</span>
          <span className="text-[10px] text-gray-400 shrink-0">{LABELS_STATUT_PROSPECT[prospect.statut]}</span>
        </div>
        {sousTitre && <p className="text-xs text-gray-500 truncate mt-0.5">{sousTitre}</p>}
      </div>
      <span className={`text-xs font-semibold shrink-0 ${metaCouleur}`}>{meta}</span>
      <ChevronRight size={14} className="text-gray-300 group-hover:text-emerald-500 transition-colors shrink-0" />
    </button>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
        <Zap size={16} className="text-amber-500 shrink-0" />
        <p className="text-sm font-semibold text-amber-800">
          {total} action{total > 1 ? 's' : ''} en attente
        </p>
      </div>

      {actionsAFaire.length > 0 && (
        <div>
          <SectionHeader icon={<CalendarClock size={16} />} titre="Actions planifiees — dues" count={actionsAFaire.length} couleur="text-red-700" bgCouleur="bg-red-50 border-red-200" />
          <div className="space-y-2">
            {actionsAFaire.map((p) => (
              <LigneProspect key={p.id} prospect={p} meta={formatDateAction(p.dateProchaineAction!)} metaCouleur="text-red-600" sousTitre={p.prochaineAction} />
            ))}
          </div>
        </div>
      )}

      {brouillonsEnAttente.length > 0 && (
        <div>
          <SectionHeader icon={<Clock size={16} />} titre="Brouillons non envoyes — +48h" count={brouillonsEnAttente.length} couleur="text-amber-700" bgCouleur="bg-amber-50 border-amber-200" />
          <div className="space-y-2">
            {brouillonsEnAttente.map(({ prospect, mail }) => (
              <LigneProspect key={`${prospect.id}-${mail.id}`} prospect={prospect} meta={formatDateRelative(mail.date?.toDate?.() || new Date())} metaCouleur="text-amber-600" sousTitre={mail.objet} />
            ))}
          </div>
        </div>
      )}

      {inactifs.length > 0 && (
        <div>
          <SectionHeader icon={<AlertCircle size={16} />} titre="Sans contact depuis +14 jours" count={inactifs.length} couleur="text-orange-700" bgCouleur="bg-orange-50 border-orange-200" />
          <div className="space-y-2">
            {inactifs.map((p) => (
              <LigneProspect key={p.id} prospect={p} meta={formatDateRelative(getDerniereActivite(p))} metaCouleur="text-orange-600" sousTitre={p.prochaineAction || undefined} />
            ))}
          </div>
        </div>
      )}

      {reponsesEnAttente.length > 0 && (
        <div>
          <SectionHeader icon={<RotateCcw size={16} />} titre="Reponses recues — relance non generee" count={reponsesEnAttente.length} couleur="text-emerald-700" bgCouleur="bg-emerald-50 border-emerald-200" />
          <div className="space-y-2">
            {reponsesEnAttente.map((p) => (
              <LigneProspect key={p.id} prospect={p} meta="Reponse en attente" metaCouleur="text-emerald-600" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// ONGLET GUIDE (identique à CrmPipelinePage)
// Réutilise le contenu existant via import identique
// ============================================

// Composant Guide simplifié pour l'apporteur — contenu identique à CrmPipelinePage
// Pour éviter la duplication complète, l'OngletGuide est importé depuis CrmPipelinePage
// Note : si séparation stricte nécessaire, copier OngletGuide ici à la prochaine session
const OngletGuideApporteur: React.FC = () => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErreur, setPdfErreur] = useState(false);

  const telechargerPdf = async () => {
    setPdfLoading(true);
    setPdfErreur(false);
    try {
      const storage = getStorage();
      const pdfRef = ref(storage, 'assets/presentation_copro_protect.pdf');
      const url = await getDownloadURL(pdfRef);
      window.open(url, '_blank');
    } catch (err) {
      console.error('[GuideApporteur] Erreur téléchargement PDF:', err);
      setPdfErreur(true);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={28} />
          <h2 className="text-2xl font-bold">Guide du CRM Prospection</h2>
        </div>
        <p className="text-emerald-100 text-sm mb-4">
          Ce guide decrit le fonctionnement complet du CRM Copro Protect — de l'identification d'un prospect jusqu'a sa conversion.
        </p>
        <button
          onClick={telechargerPdf}
          disabled={pdfLoading}
          className="flex items-center gap-2 px-4 py-2.5 bg-white text-emerald-700 rounded-lg text-sm font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-60"
        >
          <FileText size={16} />
          {pdfLoading ? 'Chargement...' : 'Telecharger la presentation Copro Protect (PDF)'}
        </button>
        {pdfErreur && (
          <p className="text-red-200 text-xs mt-2">Impossible de charger le PDF. Contacte Dominique.</p>
        )}
      </div>

      {[
        {
          titre: 'Pipeline',
          desc: 'Le tableau Kanban. Chaque colonne represente une etape de la relation commerciale. Les prospects progressent de colonne en colonne au fil des echanges.',
        },
        {
          titre: 'Actions du jour',
          desc: 'La vue operationnelle quotidienne. Repond a la question : que dois-je faire maintenant ? Affiche les actions urgentes en 4 sections : actions planifiees en retard, brouillons +48h, prospects sans contact +14j, reponses sans relance.',
        },
        {
          titre: 'Messagerie',
          desc: 'Tous les mails generes par SENTINELLE, classes par statut (brouillon ou envoye). Un badge orange signale les brouillons en attente d\'envoi.',
        },
        {
          titre: 'Prospects',
          desc: 'La liste complete de vos prospects avec leurs informations principales. Un clic ouvre la fiche detaillee avec l\'historique et SENTINELLE CRM.',
        },
      ].map((item) => (
        <div key={item.titre} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="font-bold text-gray-800 mb-1">{item.titre}</p>
          <p className="text-sm text-gray-600">{item.desc}</p>
        </div>
      ))}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <p className="font-bold text-blue-800 mb-3 text-sm uppercase tracking-wide">Les 5 piliers SCUD</p>
        {[
          { pilier: '1 — Posture du beotien naif', desc: 'Se positionner comme quelqu\'un qui decouvre et cherche conseil. Desarme l\'interlocuteur.' },
          { pilier: '2 — Flatter le professionnalisme', desc: 'Reconnaitre explicitement l\'expertise. Cree une obligation morale de repondre.' },
          { pilier: '3 — Questions, pas affirmations', desc: 'Ne jamais affirmer ce que l\'autre devrait faire. Une question ne peut pas etre attaquee.' },
          { pilier: '4 — Documenter sans interpreter', desc: 'S\'appuyer sur des faits connus sans extrapoler ni sur-promettre.' },
          { pilier: '5 — Remercier d\'abord', desc: 'Commencer par reconnaitre ce que fait deja l\'interlocuteur. Etablit un climat de respect.' },
        ].map((item) => (
          <div key={item.pilier} className="border-l-2 border-blue-300 pl-3 mb-3">
            <p className="font-semibold text-blue-700 text-xs">{item.pilier}</p>
            <p className="text-gray-600 text-xs mt-0.5">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// PAGE PRINCIPALE — APPORTEUR PIPELINE (harmonisee)
// ============================================

const ApporteurPipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // ---- État global ----
  const [ongletActif, setOngletActif] = useState<OngletApporteur>('pipeline');
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [responsables, setResponsables] = useState<InfoResponsable[]>([]);
  const [loading, setLoading] = useState(true);

  // ---- État Pipeline ----
  const [filtreRecherche, setFiltreRecherche] = useState('');
  const [filtreSegment, setFiltreSegment] = useState<SegmentProspect | 'tous'>('tous');
  const [showPerdus, setShowPerdus] = useState(false);
  const draggedProspect = useRef<Prospect | null>(null);

  // ---- État modales ----
  const [modalCreation, setModalCreation] = useState(false);
  const [prospectEdition, setProspectEdition] = useState<Prospect | null>(null);
  const [prospectMail, setProspectMail] = useState<Prospect | null>(null);
  const [prospectAMarquerContacte, setProspectAMarquerContacte] = useState<Prospect | null>(null);

  // ---- Identifiant apporteur ----
  const apporteurId: ResponsableProspect =
    (userProfile?.apporteurInfo?.identifiant as ResponsableProspect) || 'alain';

  // ---- Chargement ----
  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const p = await chargerProspectsParResponsable(apporteurId);
      setProspects(p);
      // chargerResponsables() nécessite un accès à users/ non autorisé pour les apporteurs
      // L'apporteur est son propre responsable — getLabelResponsable utilise le fallback identifiant
      setResponsables([]);
    } catch (err) {
      console.error('[CRM Apporteur] Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  }, [apporteurId]);

  useEffect(() => {
    charger();
  }, [charger]);

  // ---- Filtrage Pipeline ----
  const prospectsFiltres = prospects.filter((p) => {
    if (filtreSegment !== 'tous' && p.segment !== filtreSegment) return false;
    if (filtreRecherche) {
      const search = filtreRecherche.toLowerCase();
      return (
        p.nom.toLowerCase().includes(search) ||
        (p.contactNom || '').toLowerCase().includes(search) ||
        (p.contactEmail || '').toLowerCase().includes(search) ||
        (p.objectifContact || '').toLowerCase().includes(search) ||
        (p.problematiques || []).some((prob) => prob.toLowerCase().includes(search))
      );
    }
    return true;
  });

  const ORDRE_SEGMENT_KANBAN: Record<string, number> = {
    ambassadeur: 0,
    client_direct: 1,
    recommandeur: 2,
  };

  const prospectParStatut = (statut: StatutProspect): Prospect[] =>
    prospectsFiltres
      .filter((p) => p.statut === statut)
      .sort((a, b) => (ORDRE_SEGMENT_KANBAN[a.segment] ?? 9) - (ORDRE_SEGMENT_KANBAN[b.segment] ?? 9));

  // ---- Drag & Drop ----
  const handleDragStart = (_e: React.DragEvent, prospect: Prospect) => {
    draggedProspect.current = prospect;
  };

  const handleDrop = async (nouveauStatut: StatutProspect) => {
    const prospect = draggedProspect.current;
    if (!prospect || prospect.statut === nouveauStatut) return;
    setProspects((prev) => prev.map((p) => (p.id === prospect.id ? { ...p, statut: nouveauStatut } : p)));
    try {
      await changerStatutProspect(prospect.id, nouveauStatut);
      if (nouveauStatut === 'converti' && prospect.contactEmail) {
        const result = await rapprocherProspectUser(prospect.id);
        if (result.found) console.log(`[CRM Apporteur] Rapprochement auto: ${result.userId}`);
      }
      await charger();
    } catch (err) {
      console.error('[CRM Apporteur] Erreur changement statut:', err);
      await charger();
    }
  };

  // ---- Actions ----
  const handleCreer = async (data: Parameters<typeof creerProspect>[0]) => {
    await creerProspect(data);
    await charger();
  };

  const handleModifier = async (data: Parameters<typeof creerProspect>[0]) => {
    if (!prospectEdition) return;
    await mettreAJourProspect(prospectEdition.id, {
      nom: data.nom,
      typeProspect: data.typeProspect,
      typeCible: data.typeCible,
      responsable: apporteurId,
      contactNom: data.contactNom || '',
      contactEmail: data.contactEmail || '',
      contactTelephone: data.contactTelephone || '',
      adresse: data.adresse || '',
      siteWeb: data.siteWeb || '',
      source: data.source || '',
      problematiques: data.problematiques || [],
      objectifContact: data.objectifContact || '',
      tailleStructure: data.tailleStructure || '',
      prochaineAction: data.prochaineAction || '',
      dateProchaineAction: data.dateProchaineAction || undefined,
    });
    setProspectEdition(null);
    await charger();
  };

  const handleNaviguerVersFiche = (prospect: Prospect) => {
    navigate(`/apporteur/crm/prospect/${prospect.id}`);
  };

  const handleMailGenere = async () => {
    const fresh = await chargerProspectsParResponsable(apporteurId);
    setProspects(fresh);
  };

  const colonnesAffichees = showPerdus ? [...COLONNES_KANBAN, 'perdu' as StatutProspect] : COLONNES_KANBAN;

  // ---- Badges onglets ----
  const badgeActionsDuJour = (() => {
    const actifs = prospects.filter((p) => p.statut !== 'converti' && p.statut !== 'perdu');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const n1 = actifs.filter((p) => {
      if (!p.dateProchaineAction) return false;
      const d = p.dateProchaineAction.toDate(); d.setHours(0, 0, 0, 0);
      return d <= today;
    }).length;
    const seuilB = new Date(today.getTime() - 48 * 60 * 60 * 1000);
    const n2 = actifs.reduce((acc, p) => acc + (p.historiqueMails || []).filter((m) => !m.envoye && (!m.type || m.type === 'brouillon') && (m.date?.toDate?.() || new Date()) < seuilB).length, 0);
    const seuilI = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
    const n3 = actifs.filter((p) => getDerniereActivite(p) < seuilI).length;
    const n4 = actifs.filter((p) => hasReponseEnAttente(p)).length;
    return (n1 + n2 + n3 + n4) || undefined;
  })();

  const badgeMessagerie = prospects.flatMap((p) => p.historiqueMails || []).filter((m) => !m.envoye).length || undefined;

  // ---- Définition des onglets ----
  const onglets: Array<{ id: OngletApporteur; label: string; icon: React.ReactNode; badge?: number }> = [
    { id: 'pipeline', label: 'Pipeline', icon: <Kanban size={16} /> },
    { id: 'actions-du-jour', label: 'Actions du jour', icon: <Zap size={16} />, badge: badgeActionsDuJour },
    { id: 'messagerie', label: 'Messagerie', icon: <Inbox size={16} />, badge: badgeMessagerie },
    { id: 'prospects', label: 'Prospects', icon: <List size={16} /> },
    { id: 'guide', label: 'Guide', icon: <BookOpen size={16} /> },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">CRM Prospection</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {prospects.length} prospect{prospects.length > 1 ? 's' : ''} — {prospects.filter((p) => p.statut === 'converti').length} converti{prospects.filter((p) => p.statut === 'converti').length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={charger} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Rafraichir">
            <RefreshCw size={18} />
          </button>
          <button onClick={() => setModalCreation(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold text-sm transition-colors shadow-sm">
            <Plus size={18} /> Nouveau prospect
          </button>
        </div>
      </div>

      {/* Barre d'onglets */}
      <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm p-1 border border-gray-200">
        {onglets.map((onglet) => (
          <button key={onglet.id} onClick={() => setOngletActif(onglet.id)}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              ongletActif === onglet.id
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}>
            {onglet.icon}
            {onglet.label}
            {onglet.badge !== undefined && onglet.badge > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                ongletActif === onglet.id
                  ? 'bg-white text-emerald-600'
                  : onglet.id === 'actions-du-jour'
                    ? 'bg-red-500 text-white'
                    : 'bg-amber-500 text-white'
              }`}>
                {onglet.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : (
        <>
          {/* ---- PIPELINE ---- */}
          {ongletActif === 'pipeline' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl shadow-sm">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={filtreRecherche} onChange={(e) => setFiltreRecherche(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                    placeholder="Rechercher un prospect..." />
                </div>
                {/* Filtre segment */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  {([
                    { val: 'tous', label: 'Tous segments' },
                    { val: 'ambassadeur', label: 'Ambassadeurs' },
                    { val: 'client_direct', label: 'Clients directs' },
                    { val: 'recommandeur', label: 'Recommandeurs' },
                  ] as const).map(({ val, label }) => (
                    <button key={val} onClick={() => setFiltreSegment(val)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                        filtreSegment === val
                          ? val === 'ambassadeur' ? 'bg-white text-emerald-700 shadow-sm'
                          : val === 'client_direct' ? 'bg-white text-purple-700 shadow-sm'
                          : val === 'recommandeur' ? 'bg-white text-blue-700 shadow-sm'
                          : 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowPerdus(!showPerdus)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border transition-colors ${showPerdus ? 'bg-red-50 text-red-600 border-red-200' : 'text-gray-400 border-gray-200 hover:text-gray-600'}`}>
                  {showPerdus ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  Perdus ({prospects.filter((p) => p.statut === 'perdu').length})
                </button>
              </div>

              {prospects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl shadow-sm gap-3">
                  <Target size={48} className="text-gray-300" />
                  <h3 className="text-lg font-bold text-gray-700">Pipeline vide</h3>
                  <p className="text-sm text-gray-500 text-center max-w-md">
                    Cree ton premier prospect pour demarrer ta prospection.
                  </p>
                  <button onClick={() => setModalCreation(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition-colors shadow-sm">
                    <Plus size={18} /> Nouveau prospect
                  </button>
                </div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-4">
                  {colonnesAffichees.map((statut) => (
                    <ColonneKanban
                      key={statut}
                      statut={statut}
                      prospects={prospectParStatut(statut)}
                      responsables={responsables}
                      onDragStart={handleDragStart}
                      onDrop={handleDrop}
                      onOuvrirDetail={handleNaviguerVersFiche}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---- ACTIONS DU JOUR ---- */}
          {ongletActif === 'actions-du-jour' && (
            <OngletActionsDuJour
              prospects={prospects}
              responsables={responsables}
              onOuvrirProspect={handleNaviguerVersFiche}
            />
          )}

          {/* ---- MESSAGERIE ---- */}
          {ongletActif === 'messagerie' && (
            <OngletMessagerie
              prospects={prospects}
              responsables={responsables}
              onOuvrirProspect={handleNaviguerVersFiche}
              onGenererMail={(p) => setProspectMail(p)}
              onGmailOuvert={(p) => setProspectAMarquerContacte(p)}
              onSupprime={charger}
            />
          )}

          {/* ---- PROSPECTS ---- */}
          {ongletActif === 'prospects' && (
            <OngletProspects
              prospects={prospects}
              responsables={responsables}
              onOuvrirDetail={handleNaviguerVersFiche}
              onCreer={() => setModalCreation(true)}
            />
          )}

          {/* ---- GUIDE ---- */}
          {ongletActif === 'guide' && <OngletGuideApporteur />}
        </>
      )}

      {/* Modales */}
      {modalCreation && (
        <ModalProspectApporteur
          responsables={responsables}
          apporteurId={apporteurId}
          onFermer={() => setModalCreation(false)}
          onSauvegarder={handleCreer}
        />
      )}
      {prospectEdition && (
        <ModalProspectApporteur
          prospect={prospectEdition}
          responsables={responsables}
          apporteurId={apporteurId}
          onFermer={() => setProspectEdition(null)}
          onSauvegarder={handleModifier}
        />
      )}
      {prospectMail && (
        <ModalMailSentinelle
          prospect={prospectMail}
          onFermer={() => setProspectMail(null)}
          onMailGenere={handleMailGenere}
          onGmailOuvert={(p) => setProspectAMarquerContacte(p)}
        />
      )}

      {/* Popup marquer contacté */}
      {prospectAMarquerContacte && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Gmail ouvert</p>
                <p className="text-sm text-gray-500">Marquer <span className="font-semibold">{prospectAMarquerContacte.nom}</span> comme contacte ?</p>
              </div>
            </div>
            <p className="text-xs text-gray-400">La carte glissera automatiquement dans la colonne "Contacte".</p>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try { await changerStatutProspect(prospectAMarquerContacte.id, 'contacte'); charger(); } catch { /* non bloquant */ }
                  setProspectAMarquerContacte(null);
                }}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
                Oui, marquer contacte
              </button>
              <button
                onClick={() => setProspectAMarquerContacte(null)}
                className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors">
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApporteurPipelinePage;
